// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';

// Import dom-ready
import domReady from '@wordpress/dom-ready';

// In-plugin notifications stylesheet.
import './style.scss';

/**
 * Class representing a notifications controller.
 *
 * This class handles the display and dismissal of notifications in a user interface. It provides methods to register actions for navigation and dismissal buttons, dismiss notifications, and update the navigation.
 *
 * @property {Object} state - The state of the notifications controller, including the count of items and the last index.
 */
class Notifications {
	/**
	 * Constructs a new instance of the NotificationController.
	 *
	 * If there are no notifications, the constructor returns early.
	 * Otherwise, it registers actions, initializes the state with the items count and last index from the global `uapNotifications` object, and updates the navigation.
	 */
	constructor() {
		// If no notification, bail.
		if ( ! this.hasNotification ) {
			return;
		}

		// Register click event listeners for the next, previous, and dismiss buttons.
		this.registerActions();

		// Initialize the state with the items count and last index from the global `uapNotifications` object.
		this.state = {
			itemsCount: parseInt( uapNotifications.itemsCount ),
			lastIndex: parseInt( uapNotifications.lastIndex )
		}

		// Update the navigation buttons in the notifications list.
		this.updateNavigation();
	}

	/**
	 * Registers click event listeners for the next, previous, and dismiss buttons in the notification controller.
	 *
	 * For the next and previous buttons, the function adds or removes the 'active' class to show or hide notifications.
	 * For the dismiss button, the function calls the `dismissNotification` method with the notification ID and the clicked element.
	 */
	registerActions() {

		var controller = this;

		let nextBtns = document.getElementsByClassName( 'uap-notifications-controller-next' );

		let prevBtns = document.getElementsByClassName( 'uap-notifications-controller-prev' );

		let dismiss = document.getElementsByClassName( 'uap-notifications-action-dismiss' );

		// Next buttons.
		Array.from( nextBtns ).forEach( function( element ) {

			element.addEventListener( 'click', function() {

				let targetParent = element.parentNode.parentNode.parentNode.parentNode;

				targetParent.classList.remove( 'active' );

				targetParent.nextElementSibling.classList.add( 'active' );

				element.parentNode.parentNode.parentNode.classList.remove( 'active' );

			} );

		} );

		// Previous buttons.
		Array.from( prevBtns ).forEach( function( element ) {

			element.addEventListener( 'click', function() {

				let targetParent = element.parentNode.parentNode.parentNode.parentNode;

				targetParent.classList.remove( 'active' );

				targetParent.previousElementSibling.classList.add( 'active' );

				element.parentNode.parentNode.parentNode.classList.remove( 'active' );

			} );

		} );

		// Dismiss
		Array.from( dismiss ).forEach( function( element ) {

			element.addEventListener( 'click', function() {
				let notification_id = element.getAttribute( 'data-notification-id' );
				controller.dismissNotification( notification_id, element );
			} );

		} );
	}

	/**
	 * Dismisses a notification and updates the UI accordingly.
	 *
	 * @param {String} notification_id - The ID of the notification to dismiss.
	 * @param {HTMLElement} element - The element that triggered the dismissal.
	 *
	 * This function sends an AJAX request to the server to dismiss the notification. If the request is successful, it removes the notification from the UI and updates the navigation. If the request fails, it logs the error message.
	 */
	dismissNotification( notification_id, element ) {

		element.setAttribute( 'loading', true );

		fetch( UncannyAutomatorBackend.ajax.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Cache-Control': 'no-cache'
            },
            body: new URLSearchParams({
                action: 'automator_notification_dismiss',
                nonce: UncannyAutomatorBackend.ajax.nonce,
            } )
        })
            .then( ( response ) => response.json() )
			.then( ( response ) => {
				if ( response.success ) {

					let currentItem = element.parentNode.parentNode.parentNode;

					currentItem.classList.remove( 'active' );

					// Activate next item.
					if ( parseInt( currentItem.getAttribute( 'data-index' ) ) !== this.state.lastIndex ) {
						if ( currentItem.nextElementSibling ) {
							currentItem.nextElementSibling.classList.add( 'active' );
						} else {
							// Go back to first element if there is no sibling.
							document
								.querySelector( 'ul.uap-notifications-list li:first-child' )
								.classList.add( 'active' );
						}
					} else {
						// Go back to the first element if state has last index.
						document
							.querySelector( 'ul.uap-notifications-list li:first-child' )
							.classList.add( 'active' );
					}

					// Delete all element if there are no remaining.
					if ( this.state.itemsCount === 1 ) {
						document.querySelector( '.uap-notifications' ).remove();
					}

					this.state.itemsCount--;

					// console.log( this.state.itemsCount );

					currentItem.remove();

					this.updateNavigation();

				}
			} )
			.catch( ( error ) => {
				console.warn(error);
			} )
			.finally( () => {
				element.removeAttribute( 'loading' );
			} );
	}

	/**
	 * Updates the navigation buttons in the notifications list.
	 *
	 * This function selects the previous and next buttons in the first and last notifications in the list, respectively. If these buttons exist, it disables them.
	 */
	updateNavigation() {

		let $prevButton = document.querySelector( 'ul.uap-notifications-list li:first-child' )
			.querySelector( '.uap-notifications-controller-prev' );

		let $nextButton = document.querySelector( 'ul.uap-notifications-list li:last-child' )
			.querySelector( '.uap-notifications-controller-next' );

		if ( $prevButton ) {
			$prevButton.setAttribute( 'disabled', true );
		}

		if ( $nextButton ) {
			$nextButton.setAttribute( 'disabled', true );
		}

	}

	/**
	 * Checks if the notifications wrapper element exists in the document.
	 *
	 * @return {Boolean} - Returns true if the notifications wrapper element exists, false otherwise.
	 */
	get hasNotification() {

		return isDefined( document.getElementById( 'uap-notifications-wrap' ) );

	}

}

// Invoke classes when the DOM is ready
domReady( () => {
	new Notifications();
} );
